import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import CulsightPageLoader from "../components/ui/CulsightLoader";
import {
  getDepartments,
  getHumanRiskScorecard,
} from "../api/HumanRiskScorecardApi";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar,
  XAxis, YAxis, Tooltip, Legend
} from "recharts";
import { Shield, TrendingUp, AlertTriangle, Award, Users, X, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

// Grade ke hisaab se sirf color chahiye, baaki sab (grade, label) API se aayega
const GRADE_COLORS = {
  A: "#1fa7a1",
  B: "#22c55e",
  C: "#f59e0b",
  D: "#f97316",
  F: "#ef4444",
};

const HumanRiskScorecard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectLoading, setSelectLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const selectedDepartment = searchParams.get("department") || "all";


  useEffect(() => {
    fetchDepartments();
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedDepartment]);

  const fetchDepartments = async () => {
    try {
      const response = await getDepartments();
      setDepartments(response.data.results);
    } catch (error) {
      console.error("Failed to load departments");
    }
  };

  const fetchData = async () => {
    try {
      const response = await getHumanRiskScorecard(selectedDepartment);
      setData(response.data);
    } catch (error) {
      toast.error("Failed to load scorecard data");
    } finally {
      setLoading(false);
      setSelectLoading(false);
    }
  };

  const handleDepartmentChange = (value) => {
    setSelectLoading(true);

    if (value === "all") {
      searchParams.delete("department");
    } else {
      searchParams.set("department", value);
    }

    setSearchParams(searchParams);
  };

  const clearFilter = () => {
    setSelectLoading(true);

    searchParams.delete("department");
    setSearchParams(searchParams);
  };

  const COLORS = ["#1fa7a1", "#22c55e", "#f59e0b", "#f97316", "#ef4444"];

  if (loading) {
    return <CulsightPageLoader />;
  }

  // Ab grade/label API se aa raha hai (data.score_grade, data.score_label),
  // sirf color yahan match kar rahe hain
  const scoreGrade = {
    grade: data?.score_grade || "-",
    label: data?.score_label || "",
    color: GRADE_COLORS[data?.score_grade] || "#6b7280",
  };

  const distributionData = data?.score_distribution ? [
    { name: "Excellent (750+)", value: data.score_distribution.excellent, color: "#1fa7a1" },
    { name: "Good (650-749)", value: data.score_distribution.good, color: "#22c55e" },
    { name: "Fair (500-649)", value: data.score_distribution.fair, color: "#f59e0b" },
    { name: "Poor (350-499)", value: data.score_distribution.poor, color: "#f97316" },
    { name: "Critical (<350)", value: data.score_distribution.critical, color: "#ef4444" }
  ] : [];

  return (
    <div className="relative space-y-6 animate-fadeIn" data-testid="human-risk-scorecard-page">

      {selectLoading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/70 backdrop-blur-[1px]">
          <CulsightPageLoader />
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1f1f23] tracking-tight">Human Risk Scorecard</h1>
          <p className="text-gray-500 text-sm mt-1">
            {selectedDepartment !== "all"
              ? `Filtered by: ${selectedDepartment} (${data?.total_users || 0} users)`
              : "Organization-wide security awareness score"
            }
          </p>
        </div>
        <div className="flex items-center gap-3">
          {selectedDepartment !== "all" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/department-heatmap")}
              className="gap-2"
            >
              <ArrowLeft size={16} />
              Back to Heatmap
            </Button>
          )}
          <Select value={selectedDepartment} onValueChange={handleDepartmentChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Departments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {Array.isArray(departments) && departments.map((dept) => (
                <SelectItem key={dept.id} value={dept.name}>
                  {dept.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedDepartment !== "all" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilter}
              className="gap-1 text-gray-500"
            >
              <X size={16} />
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Department Filter Badge */}
      {selectedDepartment !== "all" && (
        <div className="flex items-center gap-2 p-3 bg-[#8c3add]/5 border border-[#8c3add]/20 rounded-xl">
          <Badge className="bg-[#8c3add] text-white">
            {selectedDepartment}
          </Badge>
          <span className="text-sm text-gray-600">
            Showing risk scores for {data?.total_users || 0} users in this department
          </span>
        </div>
      )}

      {/* Main Score Card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Score Display */}
        <Card className="lg:col-span-1 border border-gray-100 shadow-sm" data-testid="main-score-card">
          <CardContent className="p-8 flex flex-col items-center justify-center">
            {/* Score Ring */}
            <div className="relative w-52 h-52">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                {/* Background circle */}
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="8"
                />
                {/* Score circle */}
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  fill="none"
                  stroke={scoreGrade.color}
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${(data?.average_score / 850) * 264} 264`}
                  className="transition-all duration-1000"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-5xl font-black" style={{ color: scoreGrade.color }}>
                  {data?.average_score || 0}
                </span>
                <span className="text-sm text-gray-500 mt-1">out of 850</span>
              </div>
            </div>

            {/* Grade Badge */}
            <div className="mt-6 text-center">
              <Badge
                className="text-2xl px-6 py-2 font-bold"
                style={{ backgroundColor: `${scoreGrade.color}15`, color: scoreGrade.color }}
              >
                {scoreGrade.grade}
              </Badge>
              <p className="mt-2 text-lg font-semibold" style={{ color: scoreGrade.color }}>
                {scoreGrade.label}
              </p>
            </div>

            {/* Trend */}
            <div className="mt-6 flex items-center gap-2 p-3 bg-green-50 rounded-xl">
              <TrendingUp size={20} className="text-green-600" />
              <span className="text-green-700 font-medium">
                +{data?.improvement_trend || 0}% from last month
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Distribution Chart */}
        <Card className="lg:col-span-2 border border-gray-100 shadow-sm" data-testid="distribution-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Score Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Pie Chart */}
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height={256}>
                  <PieChart>
                    <Pie
                      data={distributionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {distributionData.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Legend */}
              <div className="flex flex-col justify-center space-y-3">
                {distributionData.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-sm text-gray-600">{item.name}</span>
                    </div>
                    <span className="font-semibold text-[#1f1f23]">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: "Total Assessed", value: data?.summary?.total_assessed ?? 0, icon: Users, color: "#8c3add" },
          { label: "Excellent Scores", value: data?.summary?.excellent_scores ?? 0, icon: Award, color: "#1fa7a1" },
          { label: "Needs Training", value: data?.summary?.needs_training ?? 0, icon: AlertTriangle, color: "#ef4444" },
          { label: "Trend", value: `+${data?.summary?.trend ?? 0}%`, icon: TrendingUp, color: "#22c55e" }
        ].map((stat, index) => (
          <Card key={index} className="border border-gray-100 shadow-sm">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-xl" style={{ backgroundColor: `${stat.color}15` }}>
                <stat.icon size={20} style={{ color: stat.color }} />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1f1f23]">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Top Risk Users */}
      <Card className="border border-gray-100 shadow-sm" data-testid="top-risks-card">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <AlertTriangle size={20} className="text-[#ef4444]" />
            Highest Susceptibility Users
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {(data?.top_risks || []).length === 0 ? (
              <p className="text-gray-500 text-center py-8">No high-risk users identified</p>
            ) : (
              data.top_risks.map((user, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
                  data-testid={`risk-user-${index}`}
                >
                  <div className="flex items-center gap-4">
                    <Avatar className="h-12 w-12 border-2 border-red-200">
                      <AvatarFallback className="bg-red-50 text-red-600 font-semibold">
                        {user.name.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold text-[#1f1f23]">{user.name}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                      <Badge variant="outline" className="mt-1 text-xs">{user.department}</Badge>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-[#ef4444]">
                      {(user.susceptibility * 100).toFixed(0)}%
                    </p>
                    <p className="text-xs text-gray-500">Susceptibility</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Score Scale Reference */}
      <Card className="border border-gray-100 shadow-sm">
        <CardContent className="p-6">
          <p className="text-sm font-medium text-gray-500 mb-4">Score Scale Reference</p>
          <div className="flex items-center justify-between gap-2">
            {[
              { range: "0-349", grade: "F", label: "Critical", color: "#ef4444" },
              { range: "350-499", grade: "D", label: "Poor", color: "#f97316" },
              { range: "500-649", grade: "C", label: "Fair", color: "#f59e0b" },
              { range: "650-749", grade: "B", label: "Good", color: "#22c55e" },
              { range: "750-850", grade: "A", label: "Excellent", color: "#1fa7a1" }
            ].map((item, index) => (
              <div
                key={index}
                className="flex-1 p-4 rounded-xl text-center"
                style={{ backgroundColor: `${item.color}10` }}
              >
                <p className="text-2xl font-bold" style={{ color: item.color }}>{item.grade}</p>
                <p className="text-sm font-medium" style={{ color: item.color }}>{item.label}</p>
                <p className="text-xs text-gray-500 mt-1">{item.range}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HumanRiskScorecard;